import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import { TextMessage } from '@/components/chat/Message/TextMessage';
import { useMessagesMutation } from '@/hooks/useMessages';
import {
  ContactFormFieldType,
  ExtendedMessagePayload,
} from '@/types/message.types';
import {
  type CheckboxFieldProps,
  type ContactFormMessageProps,
  type PhoneFieldProps,
  type TextFieldProps,
} from './types';

const TextField = React.memo<TextFieldProps>(
  ({
    id,
    label,
    value,
    type = 'text',
    autoComplete,
    placeholder,
    required = false,
    onChange,
  }) => (
    <div className="sf:flex sf:flex-col sf:gap-1">
      <label
        htmlFor={id}
        className="sf:text-xs sf:font-semibold sf:text-gray-400 sf:uppercase"
      >
        {label}
        {required && ' *'}
      </label>

      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        className="sf:rounded-md sf:bg-white sf:border sf:border-gray-300 sf:p-2 sf:text-base sf:sm:text-sm sf:text-gray-900 sf:placeholder-gray-300"
        value={value}
        onChange={onChange}
      />
    </div>
  ),
);

TextField.displayName = 'TextField';

const PhoneField = React.memo<PhoneFieldProps>(
  ({ id, label, value, placeholder, required = false, onChange }) => (
    <div className="sf:flex sf:flex-col sf:gap-1">
      <label
        htmlFor={id}
        className="sf:text-xs sf:font-semibold sf:text-gray-400 sf:uppercase"
      >
        {label}
        {required && ' *'}
      </label>

      <PhoneInput
        country={'hk'}
        enableClickOutside={false}
        value={value}
        placeholder={placeholder}
        specialLabel=""
        onChange={onChange}
        inputProps={{
          id,
          required: true,
          autoComplete: 'none',
        }}
        inputClass="sf:placeholder-gray-300"
        inputStyle={{
          width: '100%',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: 'var(--sf-color-gray-300)',
          borderRadius: 'var(--sf-radius-md)',
          paddingTop: 'calc(var(--sf-spacing) * 2)',
          paddingRight: 'calc(var(--sf-spacing) * 2)',
          paddingBottom: 'calc(var(--sf-spacing) * 2)',
          paddingLeft: 'calc(var(--sf-spacing) * 14)',
          fontSize: 'var(--phone-input-font-size)',
          lineHeight: 'var(--phone-input-line-height)',
          height: '38px',
          color: 'var(--sf-color-gray-900)',
          backgroundColor: 'var(--sf-color-white)',
        }}
        buttonClass="sf:hover:bg-none"
        buttonStyle={{
          border: 'none',
        }}
      />
    </div>
  ),
);

PhoneField.displayName = 'PhoneField';

const CheckboxField = React.memo<CheckboxFieldProps>(
  ({ id, label, checked, required = false, onChange }) => (
    <div className="sf:flex sf:items-start">
      <input
        type="checkbox"
        id={id}
        className="sf:w-5 sf:h-5 sf:accent-widget-button-color sf:cursor-pointer"
        checked={checked}
        onChange={onChange}
        required={required}
      />

      <label
        htmlFor={id}
        className="sf:ml-2 sf:text-xs sf:text-gray-900 sf:select-none sf:cursor-pointer"
      >
        {label}
      </label>
    </div>
  ),
);

CheckboxField.displayName = 'CheckboxField';

export const ContactFormMessage = React.memo<ContactFormMessageProps>(
  ({ message }) => {
    const { t } = useTranslation('common');
    const { isSending, handleSendContactForm } = useMessagesMutation();
    const { isPhoneNumberRequired, isEmailRequired } =
      message.preChatForm || {};
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [isAgreedToMarketingConsent, setIsAgreedToMarketingConsent] =
      useState(false);

    const isFormValid =
      name.trim() !== '' &&
      (!isPhoneNumberRequired || phone.trim() !== '') &&
      (!isEmailRequired || email.trim() !== '') &&
      isAgreedToMarketingConsent;

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const payload: ExtendedMessagePayload = {
        extendedMessageType: 501,
        extendedMessagePayloadDetail: {
          liveChatV2MessageObject: {
            contact_form: {
              type: 'livechatv2_contact_form',
              fields: [
                {
                  id: 'name',
                  label: t('contactFormMessage.nameLabel'),
                  type: ContactFormFieldType.TEXT,
                  placeholder: t('contactFormMessage.namePlaceholder'),
                  required: true,
                  value: name,
                },
                {
                  id: 'email',
                  label: t('contactFormMessage.emailLabel'),
                  type: ContactFormFieldType.EMAIL,
                  placeholder: t('contactFormMessage.emailPlaceholder'),
                  required: isEmailRequired,
                  value: email,
                },
                {
                  id: 'phone',
                  label: t('contactFormMessage.phoneLabel'),
                  type: ContactFormFieldType.PHONE,
                  placeholder: t('contactFormMessage.phonePlaceholder'),
                  required: isPhoneNumberRequired,
                  value: phone,
                },
                {
                  id: 'marketing_consent',
                  label: t('contactFormMessage.marketingConsent'),
                  type: ContactFormFieldType.CHECKBOX,
                  required: true,
                  value: `${isAgreedToMarketingConsent}`,
                },
              ],
            },
          },
        },
      };

      handleSendContactForm(message.id, payload);
    };

    return (
      <>
        <TextMessage message={message} />

        <div className="sf:message-bubble sf:bg-white/60 sf:rounded-xl sf:p-2 sf:w-[var(--w-message-bubble)] sf:shadow-message-bubble">
          <form
            className="sf:flex sf:flex-col sf:gap-3"
            onSubmit={handleSubmit}
          >
            <span>
              <p className="sf:text-sm sf:text-gray-900">
                {t('contactFormMessage.description')}
              </p>
              <p className="sf:mt-1 sf:text-xs sf:text-gray-500">
                {t('contactFormMessage.requiredFields')}
              </p>
            </span>

            {/* TODO: we may need to render custom fields based on user settings */}
            <TextField
              id="name"
              label={t('contactFormMessage.nameLabel')}
              value={name}
              placeholder={t('contactFormMessage.namePlaceholder')}
              autoComplete="name"
              required
              onChange={(e) => setName(e.target.value)}
            />

            <PhoneField
              id="phone"
              label={t('contactFormMessage.phoneLabel')}
              value={phone}
              placeholder={t('contactFormMessage.phonePlaceholder')}
              required={isPhoneNumberRequired}
              onChange={(phone) => setPhone(phone)}
            />

            <TextField
              id="email"
              label={t('contactFormMessage.emailLabel')}
              type="email"
              value={email}
              placeholder={t('contactFormMessage.emailPlaceholder')}
              autoComplete="email"
              required={isEmailRequired}
              onChange={(e) => setEmail(e.target.value)}
            />

            <CheckboxField
              id="marketing_consent"
              label={t('contactFormMessage.marketingConsent')}
              checked={isAgreedToMarketingConsent}
              required
              onChange={(e) => setIsAgreedToMarketingConsent(e.target.checked)}
            />

            <button
              type="submit"
              disabled={!isFormValid || isSending}
              className="sf:flex sf:items-center sf:justify-center sf:h-[32px] sf:px-3 sf:rounded-md sf:text-sm sf:font-semibold sf:text-white sf:bg-widget-button-color sf:cursor-pointer sf:hover:opacity-80 sf:disabled:cursor-not-allowed sf:disabled:opacity-30 sf:transition-opacity sf:duration-150 sf:ease-in-out"
            >
              {isSending ? (
                <div className="sf:w-4 sf:h-4 sf:border-2 sf:border-gray-300 sf:border-t-widget-button-color sf:rounded-full sf:animate-spin" />
              ) : (
                t('contactFormMessage.send')
              )}
            </button>
          </form>
        </div>
      </>
    );
  },
);

ContactFormMessage.displayName = 'ContactFormMessage';
